import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import LumetierWordmark from "@/components/LumetierWordmark";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  async function submit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't send reset email. Try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className="min-h-screen relative overflow-hidden bg-[#070709] text-stone-100 flex items-center justify-center px-6 py-12"
      data-testid="forgot-password-page"
    >
      <div aria-hidden="true" className="pointer-events-none absolute -top-40 -left-40 w-[36rem] h-[36rem] rounded-full opacity-[0.18]" style={{ background: "radial-gradient(circle at center, hsl(var(--gold) / 0.55), transparent 60%)" }} />
      <div aria-hidden="true" className="pointer-events-none absolute -bottom-40 -right-40 w-[40rem] h-[40rem] rounded-full opacity-[0.14]" style={{ background: "radial-gradient(circle at center, hsl(var(--gold) / 0.45), transparent 60%)" }} />

      <div className="relative z-10 w-full max-w-md">
        <div className="flex flex-col items-center text-center mb-10">
          <LumetierWordmark tone="light" className="text-xl" testid="forgot-wordmark" />
          <div className="mt-2 text-[10px] uppercase tracking-[0.32em] text-stone-500">
            Studio Manager
          </div>
        </div>

        <div className="bg-white/[0.03] backdrop-blur-sm border border-white/[0.07] rounded-sm px-8 py-10 shadow-[0_30px_120px_-20px_rgba(0,0,0,0.6)]">
          {sent ? (
            <div data-testid="forgot-success">
              <h1 className="font-serif text-3xl sm:text-4xl font-light leading-tight text-stone-100">
                Check your inbox.
              </h1>
              <p className="mt-4 text-sm text-stone-400 leading-relaxed">
                If <span className="text-stone-200">{email}</span> is registered, we just sent a password reset link.
                The link is valid for 30 minutes.
              </p>
              <p className="mt-3 text-xs text-stone-500 leading-relaxed">
                Didn&apos;t get it? Check spam, or try again in a moment.
              </p>
              <Link
                to="/admin/login"
                className="mt-10 block w-full text-center h-12 leading-[3rem] bg-[hsl(var(--gold))] text-stone-900 text-xs uppercase tracking-[0.28em] font-semibold transition-all hover:bg-[hsl(var(--gold))]/90"
                data-testid="forgot-back-to-login"
              >
                Back to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={submit}>
              <h1 className="font-serif text-3xl sm:text-4xl font-light leading-tight text-stone-100">
                Forgot password?
              </h1>
              <p className="mt-3 text-sm text-stone-400 leading-relaxed">
                Enter the email on your studio account and we&apos;ll send a reset link.
              </p>

              <label className="mt-8 block">
                <span className="text-[10px] uppercase tracking-[0.28em] text-stone-500 font-medium">
                  Email
                </span>
                <input
                  required
                  type="email"
                  data-testid="forgot-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-2 w-full bg-transparent border-b border-white/[0.12] py-3 text-stone-100 placeholder-stone-600 outline-none transition-colors focus:border-[hsl(var(--gold))]"
                  autoComplete="email"
                />
              </label>

              {error && (
                <div className="mt-4 text-xs text-rose-400" data-testid="forgot-error">{error}</div>
              )}

              <button
                type="submit"
                disabled={loading}
                data-testid="forgot-submit-btn"
                className="mt-9 w-full inline-flex items-center justify-center h-12 bg-[hsl(var(--gold))] text-stone-900 text-xs uppercase tracking-[0.28em] font-semibold transition-all hover:bg-[hsl(var(--gold))]/90 hover:shadow-[0_8px_30px_-10px_hsl(var(--gold))] disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? "Sending…" : "Send reset link"}
              </button>

              <Link
                to="/admin/login"
                className="mt-6 block text-center text-[10px] uppercase tracking-[0.28em] text-stone-500 hover:text-stone-200 transition-colors"
                data-testid="forgot-back-link"
              >
                ← Back to sign in
              </Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
